<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageTimestamp } from 'shared/helpers/timeHelper';

import MessageStatus from './MessageStatus.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';

import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';

const { t } = useI18n();

const {
  isAFacebookInbox,
  isALineChannel,
  isAPIInbox,
  isASmsInbox,
  isATelegramChannel,
  isATwilioChannel,
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAnEmailChannel,
  isAnInstagramChannel,
  isATiktokChannel,
} = useInbox();

const {
  status,
  isPrivate,
  createdAt,
  updatedAt,
  sourceId,
  messageType,
  contentAttributes,
} = useMessageContext();

const formatTime = sec => messageTimestamp(sec, 'LLL d, h:mm a');

const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  if (contentAttributes.value?.deleted) return false;

  if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
  if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

  return false;
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (isAnEmailChannel.value) return !!sourceId.value;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isASmsInbox.value ||
    isATelegramChannel.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.SENT;

  if (isALineChannel.value) return true;

  return false;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isASmsInbox.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.DELIVERED;
  if (isAWebWidgetInbox.value) {
    return status.value === MESSAGE_STATUS.SENT;
  }
  if (isALineChannel.value) {
    return status.value === MESSAGE_STATUS.DELIVERED;
  }

  return false;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.READ;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.READ;
  }

  return false;
});

const statusToShow = computed(() => {
  if (isRead.value) return MESSAGE_STATUS.READ;
  if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
  if (isSent.value) return MESSAGE_STATUS.SENT;

  return MESSAGE_STATUS.PROGRESS;
});

const deliveryTimelineTooltip = computed(() => {
  if (!showStatusIndicator.value) return '';
  if (status.value === MESSAGE_STATUS.FAILED) return '';
  if (status.value === MESSAGE_STATUS.PROGRESS) {
    return t('CHAT_LIST.SENDING');
  }

  const parts = [
    t('CONVERSATION.MESSAGE_DELIVERY.SENT_AT', {
      time: formatTime(createdAt.value),
    }),
  ];

  if (
    status.value === MESSAGE_STATUS.DELIVERED &&
    updatedAt.value &&
    updatedAt.value !== createdAt.value
  ) {
    parts.push(
      t('CONVERSATION.MESSAGE_DELIVERY.DELIVERED_AT', {
        time: formatTime(updatedAt.value),
      })
    );
  }

  if (status.value === MESSAGE_STATUS.READ && updatedAt.value) {
    parts.push(
      t('CONVERSATION.MESSAGE_DELIVERY.SEEN_AT', {
        time: formatTime(updatedAt.value),
      })
    );
  }

  return parts.join('\n');
});

const receivedAtTooltip = computed(() =>
  t('CONVERSATION.MESSAGE_DELIVERY.RECEIVED_AT', {
    time: formatTime(createdAt.value),
  })
);

const privateNoteTooltip = computed(() => {
  if (!isPrivate.value) return '';
  return [
    t('CONVERSATION.REPLYBOX.PRIVATE_NOTE'),
    t('CONVERSATION.MESSAGE_DELIVERY.SENT_AT', {
      time: formatTime(createdAt.value),
    }),
  ].join('\n');
});

const showReceivedClock = computed(() => {
  if (showStatusIndicator.value) return false;
  if (isPrivate.value) return false;
  if (contentAttributes.value?.deleted) return false;
  if (messageType.value === MESSAGE_TYPES.ACTIVITY) return false;
  return true;
});
</script>

<template>
  <div
    class="flex min-h-[14px] flex-col items-center justify-center gap-1 text-xs leading-none"
  >
    <MessageStatus
      v-if="showStatusIndicator"
      :status="statusToShow"
      :timeline-tooltip="deliveryTimelineTooltip"
    />
    <span
      v-else-if="isPrivate"
      v-tooltip="privateNoteTooltip"
      class="inline-flex items-center justify-center"
    >
      <Icon icon="i-lucide-lock-keyhole" class="size-3.5 text-n-slate-10" />
    </span>
    <span
      v-else-if="showReceivedClock"
      v-tooltip="receivedAtTooltip"
      class="inline-flex items-center justify-center"
    >
      <Icon icon="i-lucide-clock-3" class="size-3.5 text-n-slate-10" />
    </span>
  </div>
</template>
